import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <>
        <SEOHead
          title="文章未找到 - 直播互动体验平台"
          description="抱歉，您查找的文章不存在或已被移除。"
        />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-file-unknow-line text-2xl"></i>
              </div>
              <h1 className="text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
              <p className="text-foreground-600 text-sm mb-6">抱歉，您查找的文章不存在或已被移除。</p>
              <Link
                to="/"
                className="inline-block px-6 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article.slug, article.categoryId);

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: article.categoryName, path: `/category/${article.categoryId}` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '直播互动体验平台',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.path ? `https://rtxrj.cn${item.path}` : undefined,
    })),
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: article.title,
        acceptedAnswer: {
          '@type': 'Answer',
          text: article.excerpt,
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 直播互动体验平台`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://rtxrj.cn/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        ogUrl={`https://rtxrj.cn/article/${article.slug}`}
        ogImage={article.image}
        schemaData={[articleSchema, breadcrumbSchema, faqSchema]}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100">
            <div className="w-full px-4 md:px-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>
          </div>

          <article className="w-full py-8 md:py-12">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <div className="mb-6">
                  <Link
                    to={`/category/${article.categoryId}`}
                    className="inline-block px-3 py-1 text-xs font-medium text-primary-700 bg-primary-100 rounded-full mb-4"
                  >
                    {article.categoryName}
                  </Link>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-serif leading-tight mb-4">
                    {article.title}
                  </h1>
                  <div className="flex items-center gap-3 text-sm text-foreground-500">
                    <img
                      src={article.authorAvatar}
                      alt={article.author}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <span className="font-medium text-foreground-700">{article.author}</span>
                    <span>·</span>
                    <span>{article.publishDate}</span>
                    <span>·</span>
                    <span>{article.readTime}阅读</span>
                  </div>
                </div>

                <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden mb-8">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div
                  className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                    [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:font-serif
                    [&_p]:mb-4 [&_p]:text-foreground-700
                    [&_strong]:text-primary-600"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                ></div>

                <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-background-200">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs text-accent-700 bg-accent-100 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full py-10 md:py-14 bg-background-100">
              <div className="w-full px-4 md:px-6">
                <div className="max-w-5xl mx-auto">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-serif mb-8">
                    相关<strong>推荐</strong>
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {relatedArticles.map((related) => (
                      <Link
                        key={related.slug}
                        to={`/article/${related.slug}`}
                        className="group bg-background-50 rounded-lg overflow-hidden hover:bg-background-200/70 transition-colors duration-300 cursor-pointer"
                      >
                        <div className="w-full h-40 overflow-hidden">
                          <img
                            src={related.image}
                            alt={related.title}
                            title={related.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="text-sm font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 font-serif">
                            {related.title}
                          </h3>
                          <p className="text-xs text-foreground-600 line-clamp-2">
                            {related.excerpt}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}